import { formatDate, ROLE_LABEL, todayISO, WEEKDAYS_SHORT } from "@love-cpmd/core";
import { Avatar, Badge, Button, cn, Modal } from "@love-cpmd/ui";
import { ArrowLeft, Bell, CalendarDays, ChevronRight, LogOut, Plus, UserRound } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAccess, useAuth } from "../../lib/auth";
import { useOverlay } from "../../lib/hooks";
import { useSettings } from "../../lib/queries";
import { LogoMark } from "../common/layout-bits";
import { ADMIN_NAV, MAIN_NAV } from "./nav";
import { NotificationBell, NotificationBellLink, useUnreadCount } from "./NotificationBell";
import { useResolvedMeta } from "./page-meta";

/** Desktop/tablet top bar: page title (or breadcrumb), session-day context, bell, primary action. */
export function TopBar() {
  const meta = useResolvedMeta();
  const { data: settings } = useSettings();
  const { show, enabled } = useAccess();
  const { openAction } = useOverlay();
  return (
    <header className="sticky top-0 z-30 hidden h-16 items-center justify-between gap-4 border-b border-outline-variant/30 bg-surface/85 px-6 backdrop-blur-md md:flex lg:px-8">
      <div className="flex min-w-0 items-center gap-2">
        {meta.parent ? (
          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-label-lg">
            <Link to={meta.parent.to} className="shrink-0 text-on-surface-variant hover:text-primary">
              {meta.parent.label}
            </Link>
            <ChevronRight className="size-4 shrink-0 text-outline-variant" aria-hidden />
            <span className="truncate text-on-surface">{meta.title}</span>
          </nav>
        ) : (
          <p className="truncate text-headline-sm text-on-surface">{meta.title}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <div className="hidden items-center gap-2 rounded-full bg-surface-container-low px-3 py-1.5 text-label-md text-on-surface-variant xl:flex">
          <CalendarDays className="size-4 text-primary" aria-hidden />
          <span className="text-on-surface">{formatDate(todayISO(), "medium")}</span>
          {settings && (
            <>
              <span className="text-outline-variant">•</span>
              <span>
                Weekly Sharing · {WEEKDAYS_SHORT[settings.weekday]} {settings.startTime}
              </span>
            </>
          )}
        </div>
        <NotificationBell />
        {meta.primary === "new-schedule" && show("schedule.edit") && (
          <Button leading={<Plus />} onClick={() => openAction("new-schedule")} disabled={!enabled("schedule.edit")}>
            Input schedule
          </Button>
        )}
      </div>
    </header>
  );
}

/** Mobile header: back (nested pages) or logo, title, bell, avatar → account menu. */
export function MobileHeader() {
  const meta = useResolvedMeta();
  const { user } = useAccess();
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);
  return (
    <>
      <header className="pt-safe sticky top-0 z-30 border-b border-outline-variant/30 bg-surface/90 backdrop-blur-md md:hidden">
        <div className="flex h-14 items-center gap-2 px-2">
          {meta.parent ? (
            <button
              type="button"
              onClick={() => navigate(meta.parent!.to)}
              aria-label={`Back to ${meta.parent.label}`}
              className="inline-flex size-11 items-center justify-center rounded-control text-on-surface hover:bg-surface-container"
            >
              <ArrowLeft className="size-5" />
            </button>
          ) : (
            <Link to="/dashboard" aria-label="Home" className="px-2">
              <LogoMark className="size-9" decorative />
            </Link>
          )}
          <h1 className="min-w-0 flex-1 truncate text-headline-sm text-on-surface">{meta.title}</h1>
          <NotificationBellLink />
          {user && (
            <button type="button" onClick={() => setAccountOpen(true)} aria-label="Account menu" className="rounded-full p-1.5">
              <Avatar name={user.name} seed={user.memberId} src={user.avatarUrl} size="sm" ring className="ring-2 ring-primary-fixed rounded-full" />
            </button>
          )}
        </div>
      </header>
      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} />
    </>
  );
}

/** Mobile bottom nav: 4 tabs (decision #6). Hidden on form pages. */
export function BottomNav() {
  const location = useLocation();
  if (/\/edit$/.test(location.pathname)) return null;
  return (
    <nav
      aria-label="Main"
      className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-outline-variant/40 bg-surface-container-lowest/95 backdrop-blur-md md:hidden"
    >
      <div className="grid h-16 grid-cols-4">
        {MAIN_NAV.filter((i) => i.tab).map((item) => (
          <NavLink key={item.to} to={item.to} className="group flex flex-col items-center justify-center gap-1">
            {({ isActive }) => (
              <>
                <span
                  className={cn(
                    "flex h-8 w-14 items-center justify-center rounded-full transition-colors duration-200 [&_svg]:size-[22px]",
                    isActive ? "bg-primary-fixed text-primary" : "text-on-surface-variant group-active:bg-surface-container",
                  )}
                >
                  <item.icon strokeWidth={isActive ? 2.4 : 1.9} aria-hidden />
                </span>
                <span className={cn("text-[11px] leading-none", isActive ? "font-bold text-primary" : "font-medium text-on-surface-variant")}>
                  {item.short ?? item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/** Avatar menu on mobile: My Schedule, Activity Log, Notifications, Profile, Manage System, Log out. */
export function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, can } = useAccess();
  const { logout } = useAuth();
  const unread = useUnreadCount();
  if (!user) return null;
  const links = [
    ...MAIN_NAV.filter((i) => !i.tab && (!i.cap || can(i.cap))).map((i) => ({ to: i.to, label: i.label, icon: i.icon, badge: 0 })),
    { to: "/notifications", label: "Notifications", icon: Bell, badge: unread },
    { to: "/profile", label: "Profile", icon: UserRound, badge: 0 },
    ...(can("admin.access") ? [{ to: ADMIN_NAV.to, label: ADMIN_NAV.label, icon: ADMIN_NAV.icon, badge: 0 }] : []),
  ];
  return (
    <Modal open={open} onClose={onClose} title="Account" size="sm">
      <div className="flex items-center gap-3 rounded-2xl bg-surface-container-low p-3">
        <Avatar name={user.name} seed={user.memberId} src={user.avatarUrl} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-label-lg text-on-surface">{user.name}</p>
          <p className="flex items-center gap-1.5 text-body-sm text-on-surface-variant">
            {user.memberId}
            <Badge tone={user.role === "system_manager" ? "brand" : "secondary"} size="xs">
              {ROLE_LABEL[user.role]}
            </Badge>
          </p>
        </div>
      </div>
      <ul className="mt-3 flex flex-col">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} onClick={onClose} className="flex items-center gap-3 rounded-xl px-2 py-3 text-label-lg text-on-surface hover:bg-surface-container-low">
              <span className="flex size-9 items-center justify-center rounded-xl bg-surface-container text-on-surface-variant">
                <l.icon className="size-[18px]" aria-hidden />
              </span>
              <span className="flex-1">{l.label}</span>
              {l.badge > 0 && (
                <span className="rounded-full bg-error px-1.5 py-0.5 text-[10px] font-bold text-on-error">{l.badge}</span>
              )}
              <ChevronRight className="size-4 text-outline" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
      <Button
        variant="danger"
        block
        className="mt-2 justify-start bg-error-container/30"
        leading={<LogOut />}
        onClick={() => {
          onClose();
          logout();
        }}
      >
        Log out
      </Button>
    </Modal>
  );
}

/** Floating action on mobile Schedule: the only FAB in the MVP (Input schedule). */
export function ScheduleFab() {
  const location = useLocation();
  const { show, enabled } = useAccess();
  const { openAction } = useOverlay();
  if (!location.pathname.startsWith("/schedule") || !show("schedule.edit")) return null;
  return (
    <button
      type="button"
      onClick={() => openAction("new-schedule")}
      disabled={!enabled("schedule.edit")}
      aria-label="Input schedule"
      className="fixed right-4 bottom-[calc(80px+env(safe-area-inset-bottom))] z-40 inline-flex size-14 items-center justify-center rounded-2xl bg-primary-container text-on-primary shadow-brand transition-transform active:scale-95 disabled:opacity-50 md:hidden"
    >
      <Plus className="size-6" />
    </button>
  );
}
